<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, watch } from 'vue';
import {
  init as echartsInit,
  type ECharts,
  type EChartsOption,
  type MarkPointComponentOption,
  type MarkLineComponentOption,
  type MarkAreaComponentOption,
} from 'echarts';
import type { ChartSection, ChartMarker } from '@/analysis/utils/reportSchema';

interface Props {
  section: ChartSection;
}
const props = defineProps<Props>();

const chartEl = ref<HTMLDivElement | null>(null);
let instance: ECharts | null = null;
let resizeObserver: ResizeObserver | null = null;

/** marker level → 색. 기본 info. */
const MARKER_LEVEL_COLORS: Record<NonNullable<ChartMarker['level']>, string> = {
  critical: '#f5222d',
  warning: '#faad14',
  info: '#1890ff',
};

/**
 * 차트 표기 수치 포맷 — 최대 소수점 3자리, 4번째 자리부터 버림(truncate, 반올림 아님).
 * 불필요한 trailing 0 은 표기하지 않음 (예: 79.36429650764602 → "79.364", 80 → "80").
 * 숫자가 아니면 원본을 문자열로 그대로 반환 (null/빈값 → '').
 */
const truncate3 = (value: unknown): string => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return value == null ? '' : String(value);
  return String(Math.trunc(n * 1000) / 1000);
};

/** '#rrggbb' → 'rgba(r, g, b, a)' — band 영역 반투명 채움용. */
const withAlpha = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

type SeriesMarkerOptions = {
  markPoint?: MarkPointComponentOption;
  markLine?: MarkLineComponentOption;
  markArea?: MarkAreaComponentOption;
};

/**
 * section.markers → ECharts markPoint / markLine / markArea 옵션 (line/bar 전용).
 * 첫 번째 series 에 부착. 해당 kind 의 마커가 존재할 때만 옵션 객체를 생성한다.
 */
const buildMarkerOptions = (markers: ChartMarker[] | undefined): SeriesMarkerOptions => {
  if (!markers?.length) return {};
  const points: NonNullable<MarkPointComponentOption['data']> = [];
  const lines: NonNullable<MarkLineComponentOption['data']> = [];
  const areas: NonNullable<MarkAreaComponentOption['data']> = [];
  markers.forEach((rawMarker) => {
    // LLM 이 kind 를 누락하는 경우가 있어 좌표 필드로 추론 (x+y→point, x→vline, y→hline, from/to→band)
    const inferredKind =
      rawMarker.kind ??
      (rawMarker.from !== undefined && rawMarker.to !== undefined
        ? 'band'
        : rawMarker.x !== undefined && typeof rawMarker.y === 'number'
          ? 'point'
          : rawMarker.x !== undefined
            ? 'vline'
            : typeof rawMarker.y === 'number'
              ? 'hline'
              : undefined);
    const m = { ...rawMarker, kind: inferredKind } as ChartMarker;
    // level 은 LLM 산 비신뢰 입력 — 정의된 3키(critical/warning/info) 밖의 값이 오면
    // 색이 undefined 가 되어 band 분기 withAlpha(color) 에서 TypeError → 차트 전체 렌더가 깨진다.
    // 알 수 없는 level 은 info 색으로 fallback.
    const color =
      MARKER_LEVEL_COLORS[m.level as keyof typeof MARKER_LEVEL_COLORS] ?? MARKER_LEVEL_COLORS.info;
    if (m.kind === 'point' && m.x !== undefined && typeof m.y === 'number') {
      points.push({
        name: m.label,
        coord: [m.x, m.y],
        value: m.label,
        itemStyle: { color },
        // 라벨 배경을 마커 색으로 채워, 라벨이 심볼보다 길어도 차트 배경에 묻히지 않게 한다.
        label: { backgroundColor: color },
      });
    } else if (m.kind === 'vline' && m.x !== undefined) {
      lines.push({
        xAxis: m.x,
        label: { formatter: m.label, position: 'insideEndTop' },
        lineStyle: { color, type: 'dashed', width: 2 },
      });
    } else if (m.kind === 'hline' && typeof m.y === 'number') {
      lines.push({
        yAxis: m.y,
        label: { formatter: m.label, position: 'insideEndTop' },
        lineStyle: { color, type: 'dashed', width: 2 },
      });
    } else if (m.kind === 'band' && m.from !== undefined && m.to !== undefined) {
      areas.push([
        {
          xAxis: m.from,
          name: m.label,
          itemStyle: { color: withAlpha(color, 0.12) },
        },
        { xAxis: m.to },
      ]);
    }
  });
  const opts: SeriesMarkerOptions = {};
  if (points.length) {
    opts.markPoint = {
      // 핀(기본 symbol)은 세로로 길어 데이터 최댓값에 찍히면 위쪽이 잘린다.
      // 아래를 가리키는 작은 삼각형(말풍선 꼬리) + 그 위에 얹은 라벨 칩으로 바꿔 세로 높이를 줄인다.
      // 삼각형 높이(6px)의 절반 + 2px 만큼 위로 올려, tip 이 라인에 닿지 않고 살짝 띄워 놓는다.
      symbol: 'triangle',
      symbolRotate: 180,
      symbolSize: [10, 6],
      symbolOffset: [0, -5],
      // 마커는 표기 전용 — hover 강조/커서 변화 없이 축 툴팁만 동작하도록 마우스 이벤트를 받지 않는다.
      silent: true,
      label: {
        position: 'top',
        distance: 0,
        fontSize: 10,
        color: '#fff',
        padding: [4, 6],
        borderRadius: 3,
      },
      data: points,
    };
  }
  if (lines.length) {
    opts.markLine = { symbol: 'none', data: lines };
  }
  if (areas.length) {
    opts.markArea = { data: areas };
  }
  return opts;
};

const buildOption = (s: ChartSection): EChartsOption => {
  const baseTextStyle = { fontFamily: 'inherit' };
  if (s.chartType === 'pie' || s.chartType === 'donut') {
    // pie/donut: 첫 series 의 data 만 사용. data 는 { name, value } 객체 배열을 우선 받고,
    // [name, value] tuple 도 허용 (LLM 이 가끔 다른 모양으로 보냄에 대비).
    const raw = s.series[0]?.data ?? [];
    const data = raw
      .map((d) => {
        if (typeof d === 'object' && d !== null && 'name' in d && 'value' in d) {
          return d as { name: string; value: number };
        }
        if (Array.isArray(d) && d.length >= 2) {
          return { name: String(d[0]), value: Number(d[1]) };
        }
        return null;
      })
      .filter((x): x is { name: string; value: number } => x !== null);
    return {
      tooltip: {
        trigger: 'item',
        valueFormatter: (v) => truncate3(v),
        // 툴팁 DOM 을 body 로 빼서 채팅 버블 등 부모의 overflow/z-index 스택에 잘리지 않게 한다.
        appendToBody: true,
      },
      legend: { bottom: 0, textStyle: baseTextStyle },
      series: [
        {
          type: 'pie',
          radius: s.chartType === 'donut' ? ['45%', '70%'] : '70%',
          center: ['50%', '45%'],
          data,
          label: { show: true, formatter: '{b}: {d}%' },
        },
      ],
    };
  }
  // line / bar: xAxis.categories + series.data (number[]) 를 우선. tuple 도 허용.
  const cats = s.xAxis?.categories ?? [];
  // markers 는 첫 번째 series 에만 부착 (pie/donut 은 위에서 early return — 무시).
  const markerOpts = buildMarkerOptions(s.markers);
  return {
    // bottom: legend(bottom:0, 높이 약 24px) 바로 위까지 플롯을 내려 x축 라벨과 범례 사이 빈 공간을 줄인다.
    // right: point 마커가 마지막 구간에 찍히면 라벨 칩(중앙 정렬)의 오른쪽 절반이 캔버스 밖으로 잘리므로,
    //        마커가 있을 때만 우측 여백을 넓혀 칩이 들어갈 자리를 만든다.
    grid: {
      left: 50,
      right: markerOpts.markPoint ? 48 : 24,
      top: 30,
      bottom: 32,
      containLabel: true,
    },
    tooltip: {
      trigger: 'axis',
      valueFormatter: (v) => truncate3(v),
      // 툴팁 DOM 을 body 로 빼서 채팅 버블 등 부모의 overflow/z-index 스택에 잘리지 않게 한다.
      appendToBody: true,
    },
    legend: { bottom: 0, textStyle: baseTextStyle },
    xAxis: {
      type: 'category',
      name: s.xAxis?.name,
      data: cats,
      axisLabel: { fontSize: 11 },
    },
    yAxis: {
      type: 'value',
      name: s.yAxis?.name,
      axisLabel: {
        fontSize: 11,
        formatter: (value: number) => `${truncate3(value)}${s.yAxis?.unit ?? ''}`,
      },
      // point 마커(핀+라벨)가 데이터 최댓값에 찍히면 y축 max 바로 위 grid 여백(30)만으론 잘린다.
      // grid.top을 늘리면 플롯 영역 자체가 줄어 찌그러져 보이므로, 대신 y축 max를 데이터 최댓값보다
      // 살짝 띄워 핀이 플롯 영역 안쪽 여유 공간에 그려지도록 한다 (plot 높이는 그대로 유지).
      max: markerOpts.markPoint
        ? (value: { min: number; max: number }) =>
            value.max + (value.max - value.min || Math.abs(value.max) || 10) * 0.12
        : undefined,
    },
    series: s.series.map((srs, idx) => ({
      name: srs.name,
      type: s.chartType === 'bar' ? 'bar' : 'line',
      smooth: s.chartType === 'line',
      itemStyle: srs.color ? { color: srs.color } : undefined,
      data: srs.data.map((d) => {
        if (typeof d === 'number') return d;
        if (Array.isArray(d) && d.length >= 2) return [d[0], d[1]];
        if (typeof d === 'object' && d !== null && 'value' in d) {
          return (d as { value: number }).value;
        }
        return Number(d) || 0;
      }),
      ...(idx === 0 ? markerOpts : {}),
    })),
  };
};

const renderChart = () => {
  if (!chartEl.value) return;
  if (!instance) {
    instance = echartsInit(chartEl.value);
  }
  instance.setOption(buildOption(props.section), true);
};

onMounted(() => {
  renderChart();
  if (chartEl.value && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => instance?.resize());
    resizeObserver.observe(chartEl.value);
  }
});

watch(() => props.section, renderChart, { deep: true });

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
  instance?.dispose();
  instance = null;
});
</script>

<template>
  <section class="report-chart">
    <h3 class="report-chart__title">{{ section.title }}</h3>
    <p
      v-if="section.subtitle"
      class="report-chart__subtitle"
    >
      {{ section.subtitle }}
    </p>
    <div
      ref="chartEl"
      class="report-chart__canvas"
    />
  </section>
</template>

<style lang="scss" scoped>
.report-chart {
  padding: 14px 16px 18px;
  margin: 0 0 24px;
  border: 1px solid var(--color-gray-05-03);
  border-radius: 6px;
  background: var(--color-gray-09-00);
}
.report-chart__title {
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-gray-00-11);
}
.report-chart__subtitle {
  margin: 0 0 8px;
  font-size: 12px;
  color: var(--color-gray-03-11);
}
.report-chart__canvas {
  width: 100%;
  height: 320px;
}

@media print {
  .report-chart__canvas {
    height: 280px;
  }
}
</style>
